import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import { getCsrfToken } from "../lib/csrf";
import { toast } from "../lib/toast";
import { useMe } from "./useMe";

export type NotificationItem = {
  id: number;
  userId: number | null;
  type: "session" | "security" | "system" | "app" | "support" | "general";
  title: string;
  body: string | null;
  actionLabel: string | null;
  actionHref: string | null;
  read: boolean;
  dismissed: boolean;
  sourceType: "system" | "admin" | "automated" | null;
  sourceId: number | null;
  createdAt: number; // unix epoch seconds
};

type FeedState = {
  notifications: NotificationItem[];
  total: number;
  loading: boolean;
  error?: string;
  unreadCount: number;
};

// ── Shared external store ──────────────────────────────────────────────────

let sharedState: FeedState = { notifications: [], total: 0, loading: false, unreadCount: 0 };
const listeners = new Set<(state: FeedState) => void>();

const emit = (next: FeedState) => {
  sharedState = next;
  listeners.forEach((listener) => listener(sharedState));
};

const update = (updater: (prev: FeedState) => FeedState) => {
  emit(updater(sharedState));
};

// ── Polling infrastructure ─────────────────────────────────────────────────

// Load the user's full set on every feed fetch so the client-side DataTable can
// paginate/search/sort across ALL notifications. 1000 is the server max
// (NotificationListQuerySchema); no consumer renders the raw array (the panel
// tables it, the bell filters unread + caps at 8).
const FEED_PAGE_LIMIT = 1000;

const POLL_INTERVAL_MS = 15_000; // 15 seconds
let pollTimer: ReturnType<typeof setInterval> | null = null;
let subscriberCount = 0;

/** Track whether a full refresh is in flight (avoids double-fetch). */
let fullRefreshPending = false;

const pollUnreadCount = async () => {
  try {
    const res = await fetch("/api/me/notifications/unread-count", {
      headers: { Accept: "application/json" },
      cache: "no-store",
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok && data?.ok) {
      const newCount = data.data?.count ?? 0;
      if (newCount !== sharedState.unreadCount) {
        update((prev) => ({ ...prev, unreadCount: newCount }));
        // Count changed — trigger a full feed refresh so Notification Center + topbar stay in sync.
        // Refresh even when the current list is empty: after a "clear all" the list is empty, and an
        // incoming notification must repopulate it (not just bump the badge). The poll only ticks while
        // a subscriber is mounted and the tab is visible, so this fetch is already scoped to when the
        // panel is live. `fullRefreshPending` debounces overlapping /feed fetches.
        if (!fullRefreshPending) {
          fullRefreshPending = true;
          void refreshFeedInternal().finally(() => { fullRefreshPending = false; });
        }
      }
    }
  } catch {
    // Non-critical — badge stays at previous value
  }
};

const startPolling = () => {
  if (pollTimer) return;
  pollTimer = setInterval(() => {
    if (document.hidden) return; // Pause when tab is hidden
    void pollUnreadCount();
  }, POLL_INTERVAL_MS);
};

const stopPolling = () => {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
};

// Resume polling immediately when tab becomes visible
const handleVisibilityChange = () => {
  if (!document.hidden && subscriberCount > 0) {
    void pollUnreadCount();
  }
};

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", handleVisibilityChange);
}

/**
 * Standalone feed refresh (no hook deps). Used by the poll to auto-refresh
 * the full feed when unread count changes.
 */
const refreshFeedInternal = async () => {
  try {
    const res = await fetch(`/api/me/notifications/feed?limit=${FEED_PAGE_LIMIT}`, {
      headers: { Accept: "application/json" },
      cache: "no-store",
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.ok === false) return;
    const items = Array.isArray(data?.data?.items) ? (data.data.items as NotificationItem[]) : [];
    const total = typeof data?.data?.total === "number" ? data.data.total : items.length;
    emit({
      notifications: items,
      total,
      loading: false,
      unreadCount: sharedState.unreadCount,
    });
  } catch {
    // Non-critical
  }
};

// ── Hook ───────────────────────────────────────────────────────────────────

export const useNotificationsFeed = () => {
  const { csrfToken } = useMe();
  const subscribe = useCallback((listener: (state: FeedState) => void) => {
    listeners.add(listener);
    listener(sharedState);
    return () => listeners.delete(listener);
  }, []);

  const state = useSyncExternalStore(subscribe, () => sharedState, () => sharedState);

  // Manage polling lifecycle: start when first subscriber mounts, stop when all unmount
  const mountedRef = useRef(false);
  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;
    subscriberCount++;
    if (subscriberCount === 1) {
      startPolling();
    }
    return () => {
      mountedRef.current = false;
      subscriberCount--;
      if (subscriberCount <= 0) {
        subscriberCount = 0;
        stopPolling();
      }
    };
  }, []);

  const refreshUnreadCount = useCallback(async () => {
    await pollUnreadCount();
  }, []);

  const refresh = useCallback(async (params?: { limit?: number; offset?: number }) => {
    update((prev) => ({ ...prev, loading: true, error: undefined }));
    try {
      const qs = new URLSearchParams();
      // Default to the full set so no bare refresh() re-caps to the server default.
      qs.set("limit", String(params?.limit ?? FEED_PAGE_LIMIT));
      if (params?.offset != null) qs.set("offset", String(params.offset));
      const url = `/api/me/notifications/feed?${qs.toString()}`;
      const res = await fetch(url, {
        headers: { Accept: "application/json" },
        cache: "no-store",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.ok === false) {
        throw new Error(data?.message ?? "Unable to load notifications.");
      }
      const items = Array.isArray(data?.data?.items) ? (data.data.items as NotificationItem[]) : [];
      const total = typeof data?.data?.total === "number" ? data.data.total : items.length;
      emit({
        notifications: items,
        total,
        loading: false,
        unreadCount: sharedState.unreadCount,
      });
      // Also refresh the lightweight unread count
      void refreshUnreadCount();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unable to load notifications.";
      update((prev) => ({ ...prev, loading: false, error: message }));
    }
  }, [refreshUnreadCount]);

  const markRead = useCallback(
    async (ids?: number[]) => {
      const csrf = csrfToken ?? getCsrfToken();
      if (!csrf) {
        toast.error("Missing security token. Refresh the page.");
        return;
      }

      try {
        const res = await fetch("/api/me/notifications/read", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrf,
          },
          body: JSON.stringify({ ids }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data?.ok === false) {
          throw new Error(data?.message ?? "Unable to mark notifications as read.");
        }
        // Optimistic update: mark matching items as read
        update((prev) => ({
          ...prev,
          notifications: prev.notifications.map((item) =>
            !ids || ids.includes(item.id) ? { ...item, read: true } : item
          ),
          error: undefined,
        }));
        void refreshUnreadCount();
      } catch (error) {
        const message = error instanceof Error ? error.message : "Unable to mark notifications as read.";
        toast.error(message);
      }
    },
    [csrfToken, refreshUnreadCount]
  );

  const markUnread = useCallback(
    async (ids?: number[]) => {
      const csrf = csrfToken ?? getCsrfToken();
      if (!csrf) {
        toast.error("Missing security token. Refresh the page.");
        return;
      }

      try {
        const res = await fetch("/api/me/notifications/unread", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrf,
          },
          body: JSON.stringify({ ids }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data?.ok === false) {
          throw new Error(data?.message ?? "Unable to mark notifications as unread.");
        }
        // Optimistic update: mark matching items as unread
        update((prev) => ({
          ...prev,
          notifications: prev.notifications.map((item) =>
            !ids || ids.includes(item.id) ? { ...item, read: false } : item
          ),
          error: undefined,
        }));
        void refreshUnreadCount();
      } catch (error) {
        const message = error instanceof Error ? error.message : "Unable to mark notifications as unread.";
        toast.error(message);
      }
    },
    [csrfToken, refreshUnreadCount]
  );

  const dismiss = useCallback(
    async (ids: number[]) => {
      const csrf = csrfToken ?? getCsrfToken();
      if (!csrf) {
        toast.error("Missing security token. Refresh the page.");
        return;
      }

      try {
        const res = await fetch("/api/me/notifications/dismiss", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrf,
          },
          body: JSON.stringify({ ids }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data?.ok === false) {
          throw new Error(data?.message ?? "Unable to dismiss notifications.");
        }
        // Remove dismissed items from local state
        update((prev) => ({
          ...prev,
          notifications: prev.notifications.filter((item) => !ids.includes(item.id)),
          total: Math.max(0, prev.total - ids.length),
          error: undefined,
        }));
        void refreshUnreadCount();
      } catch (error) {
        const message = error instanceof Error ? error.message : "Unable to dismiss notifications.";
        toast.error(message);
      }
    },
    [csrfToken, refreshUnreadCount]
  );

  const unreadCount = useMemo(() => state.unreadCount, [state.unreadCount]);

  return {
    notifications: state.notifications,
    total: state.total,
    loading: state.loading,
    error: state.error,
    unreadCount,
    refresh,
    refreshUnreadCount,
    markRead,
    markUnread,
    dismiss,
  };
};
